<script setup lang="ts">
import { computed } from 'vue'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import Button from '@uiKit/elements/Button.vue'
import Select from '@uiKit/elements/Select.vue'
import PiIcon from '@uiKit/elements/PiIcon.vue'
import ConfirmDialog from '@uiKit/elements/ConfirmDialog.vue'
import SearchInput from '@uiKit/components/SearchInput.vue'
import { useConfirmDialog } from '@shared/composables/useConfirmDialog'
import { FEE_LIFECYCLE_STAGE_OPTIONS, FEE_PARTY_ROLE_OPTIONS } from '@shared/constants/fees'
import type { SelectOption } from '@shared/types'
import FeeListView from './FeeListView.vue'
import FeeDetailsView from './FeeDetailsView.vue'
import FeeEditForm from './FeeEditForm.vue'
import FeeHistoryModal from './FeeHistoryModal.vue'
import type {
  FeeDefinition,
  FeeDefinitionFormValues,
  FeeDefinitionVersion,
} from '../types/feeTypes'
import type { FeeCodeInForce } from '../utils/feeFormUtils'

export type FeeConfigViewMode = 'list' | 'details' | 'edit' | 'create'

const ALL_STAGES = 'all'
const ALL_ROLES = 'all'
const STATUS_ALL = 'all'
const STATUS_ACTIVE = 'active'
const STATUS_DISABLED = 'disabled'

const props = defineProps<{
  viewMode: FeeConfigViewMode
  fees: FeeDefinition[]
  selectedFee: FeeDefinition | null
  /** `'all'` or a `FeeLifecycleStage` — the container narrows it. */
  stageFilter: string
  /** `'all' | 'active' | 'disabled'`. */
  statusFilter: string
  /** `'all'` or a `FeePartyRole` — who pays the fee. */
  paidByFilter: string
  /** `'all'` or a `FeePartyRole` — who receives it. */
  paidToFilter: string
  /** Free text matched against the fee name. */
  searchQuery: string
  /** Every market a fee can be scoped to, `{ label: name, value: id }`. */
  markets: SelectOption[]
  /** Every `(market, code)` pair already in force — the create form checks it. */
  codesInForce: FeeCodeInForce[]
  isLoading: boolean
  isSaving: boolean
  isDisabling: boolean
  isHistoryModalOpen: boolean
  historyVersions: FeeDefinitionVersion[]
  isHistoryLoading: boolean
}>()

const emit = defineEmits<{
  (e: 'stageFilterChange', stage: string): void
  (e: 'statusFilterChange', status: string): void
  (e: 'paidByFilterChange', role: string): void
  (e: 'paidToFilterChange', role: string): void
  (e: 'searchQueryChange', query: string): void
  (e: 'viewDetails', fee: FeeDefinition): void
  (e: 'viewHistory', fee: FeeDefinition): void
  (e: 'editFee', fee: FeeDefinition): void
  (e: 'createFee'): void
  (e: 'disableFee', fee: FeeDefinition): void
  (e: 'save', values: FeeDefinitionFormValues): void
  (e: 'backToList'): void
  (e: 'closeHistory'): void
}>()

const confirm = useConfirmDialog()

const stageOptions: SelectOption[] = [
  { label: 'All stages', value: ALL_STAGES },
  ...FEE_LIFECYCLE_STAGE_OPTIONS,
]

// Both role filters offer every role: any party can appear on either side of a
// fee, and the two are independent — an investor-paid fee may be received by
// the platform, a government authority or a payment gateway.
const paidByOptions: SelectOption[] = [
  { label: 'All payers', value: ALL_ROLES },
  ...FEE_PARTY_ROLE_OPTIONS,
]

const paidToOptions: SelectOption[] = [
  { label: 'All recipients', value: ALL_ROLES },
  ...FEE_PARTY_ROLE_OPTIONS,
]

const statusOptions: SelectOption[] = [
  { label: 'All statuses', value: STATUS_ALL },
  { label: 'Active only', value: STATUS_ACTIVE },
  { label: 'Disabled only', value: STATUS_DISABLED },
]

const isListView = computed(() => props.viewMode === 'list')
const historyFeeName = computed(() => props.selectedFee?.name ?? '')

function confirmDisable(fee: FeeDefinition): void {
  confirm.require({
    header: 'Disable fee',
    message: `"${fee.name}" stops being applied to new properties. Fees already settled under it keep pointing at it, so nothing historical changes.`,
    icon: 'pi pi-exclamation-triangle',
    rejectLabel: 'Keep Active',
    acceptLabel: 'Disable Fee',
    rejectClass: 'p-button-secondary p-button-outlined p-button-sm',
    acceptClass: 'p-button-danger p-button-sm',
    accept: () => emit('disableFee', fee),
  })
}
</script>

<template>
  <Box class="fee-master-root">
    <Box
      v-if="isListView"
      flex
      class="justify-content-between align-items-center flex-wrap gap-4 mb-6"
    >
      <Box>
        <Text type="page-title">Fee Configuration</Text>
        <Text type="body" color="muted" class="mt-2">
          Every fee the platform can charge, and the version of each in force today.
        </Text>
      </Box>
      <Box flex class="align-items-center gap-3 flex-wrap">
        <Box class="platform-indicator" flex>
          <Box class="access-icon-box flex-shrink-0">
            <PiIcon name="shield" class="access-icon" size="sm" />
          </Box>
          <Box>
            <Text type="caption" class="access-label">Access Level</Text>
            <Text type="body" weight="bold" class="access-value">Super Admin Only</Text>
          </Box>
        </Box>
        <Button label="New Fee" icon="pi pi-plus" @click="emit('createFee')" />
      </Box>
    </Box>

    <Box v-if="isListView" class="animate-fade-in">
      <Box flex class="align-items-center gap-3 flex-wrap mb-4 filter-row">
        <Box class="search-control">
          <SearchInput
            :model-value="props.searchQuery"
            placeholder="Search by name"
            @update:model-value="emit('searchQueryChange', $event)"
          />
        </Box>
        <Box class="filter-control">
          <Select
            :model-value="props.stageFilter"
            :options="stageOptions"
            size="small"
            @update:model-value="emit('stageFilterChange', $event)"
          />
        </Box>
        <Box class="filter-control">
          <Select
            :model-value="props.paidByFilter"
            :options="paidByOptions"
            size="small"
            @update:model-value="emit('paidByFilterChange', $event)"
          />
        </Box>
        <Box class="filter-control">
          <Select
            :model-value="props.paidToFilter"
            :options="paidToOptions"
            size="small"
            @update:model-value="emit('paidToFilterChange', $event)"
          />
        </Box>
        <Box class="filter-control">
          <Select
            :model-value="props.statusFilter"
            :options="statusOptions"
            size="small"
            @update:model-value="emit('statusFilterChange', $event)"
          />
        </Box>
        <Text type="caption" color="muted">
          {{ props.fees.length }} {{ props.fees.length === 1 ? 'fee' : 'fees' }}
        </Text>
      </Box>

      <FeeListView
        :fees="props.fees"
        :is-loading="props.isLoading"
        @view-details="emit('viewDetails', $event)"
        @view-history="emit('viewHistory', $event)"
      />
    </Box>

    <FeeDetailsView
      v-else-if="props.viewMode === 'details' && props.selectedFee"
      :fee="props.selectedFee"
      :markets="props.markets"
      :is-disabling="props.isDisabling"
      @back="emit('backToList')"
      @edit="emit('editFee', props.selectedFee)"
      @view-history="emit('viewHistory', props.selectedFee)"
      @disable="confirmDisable(props.selectedFee)"
    />

    <FeeEditForm
      v-else-if="props.viewMode === 'edit' || props.viewMode === 'create'"
      :fee="props.viewMode === 'edit' ? props.selectedFee : null"
      :markets="props.markets"
      :codes-in-force="props.codesInForce"
      :is-loading="props.isSaving"
      @back="emit('backToList')"
      @save="emit('save', $event)"
    />

    <FeeHistoryModal
      :visible="props.isHistoryModalOpen"
      :fee-name="historyFeeName"
      :versions="props.historyVersions"
      :is-loading="props.isHistoryLoading"
      @update:visible="emit('closeHistory')"
    />

    <ConfirmDialog />
  </Box>
</template>

<style scoped>
.fee-master-root {
  background: var(--p-surface-50);
  min-height: 100%;
}

.animate-fade-in {
  animation: fadeIn 0.4s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.platform-indicator {
  padding: 0.5rem 1rem;
  background: var(--p-primary-50);
  border-radius: var(--p-border-radius-lg);
  border: 1px solid var(--p-primary-100);
  align-items: center;
  gap: 0.75rem;
}

.access-icon-box {
  width: 2rem;
  height: 2rem;
  border-radius: var(--p-border-radius-md);
  background: var(--p-primary-600);
  display: flex;
  align-items: center;
  justify-content: center;
}

.access-icon {
  color: var(--p-surface-0);
}

.access-label {
  display: block;
}

.filter-control {
  width: 200px;
}

.search-control {
  width: 260px;
}

/* SearchInput defaults to 42px; match the small Selects beside it. */
.search-control :deep(.search-input.p-inputtext) {
  height: 2.25rem;
}

.filter-row {
  padding-bottom: 1rem;
  border-bottom: 1px solid var(--p-surface-100);
}
</style>
